// The Unicorn Knight's horn: geometry only. `docs/claude/armies.md`.
//
// Nothing here does damage — `reduce.ts` owns that, so doing both is a cycle.
//
// **`attacks.ts` must not learn any of this**: the horn goes through `damage`,
// which refuses a last royal, so it can never check. Perft cannot see it.

import { type Color, DIR_OFFSET, fileOf, pieceColor, rankOf } from "./board";
import { areEnemies, type Position, variantAt } from "./position";
import { canBeHurt } from "./chess/moveGen";
import { standable } from "./chess/blow";
import { clearLine } from "./chess/plow";
import { pieceVariant, UNICORN_REACH } from "./rules";

/** Does the piece on `sq` carry a horn? */
export const lancesFrom = (position: Position, sq: number): boolean =>
  pieceVariant(variantAt(position, sq)).lances === true;

/** Chebyshev, the turret's measure — one reading for both halves of the horn. */
export const hornAway = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/** Is `at` inside the horn's reach of `from`? Its own square is not. */
export const withinHorn = (from: number, at: number): boolean =>
  at !== from && hornAway(from, at) <= UNICORN_REACH;

/**
 * Where a Unicorn may shuffle before it strikes: orthogonal, one, taking nothing.
 *
 * `standable`, not a list of walls here, or it goes stale on the seventh (C13).
 */
export function lanceSteps(position: Position, unicorn: number): number[] {
  const out: number[] = [];
  for (const offset of DIR_OFFSET) {
    const to = unicorn + offset;
    if (!standable(position, to)) continue;
    // Bare ground: a lance step breaks nothing (§31 S1).
    if (position.structures.has(to)) continue;
    out.push(to);
  }
  return out;
}

/**
 * What the picket on `at` chips this round, or -1. `turretTargets`' ordering.
 *
 * **`canBeHurt` here, unlike a turret**: one slot, so a last king would soak it.
 */
export function picketTarget(position: Position, at: number, owner: Color): number {
  let best = -1;
  let bestAway = 0;
  for (const [square, piece] of position.board) {
    if (!areEnemies(position, owner, pieceColor(piece))) continue;
    if (!withinHorn(at, square)) continue;
    // Ranged: an obstruction between blocks it (§31 S1).
    if (!clearLine(position, at, square)) continue;
    if (!canBeHurt(position, square, piece)) continue;
    const away = hornAway(at, square);
    if (best < 0 || away < bestAway || (away === bestAway && square < best)) {
      best = square;
      bestAway = away;
    }
  }
  return best;
}

/** Every Unicorn `owner` has, **sorted** — a picket kills, so order is a desync. */
export function unicornSquares(position: Position, owner: Color): number[] {
  const out: number[] = [];
  for (const [square, piece] of position.board) {
    if (pieceColor(piece) !== owner) continue;
    if (!lancesFrom(position, square)) continue;
    out.push(square);
  }
  return out.sort((a, b) => a - b);
}
